import { createRef, forwardRef, Component } from 'react';
import throttle from 'lodash.throttle';
import cn from 'classnames';
import { emitOnScroll, onScrollTo } from '@ebsco-ui/scroll-emitter';
import { jsx } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _typeof2(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof2 = function _typeof2(obj) { return typeof obj; }; } else { _typeof2 = function _typeof2(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof2(obj); }

function _typeof(obj) {
  if (typeof Symbol === "function" && _typeof2(Symbol.iterator) === "symbol") {
    _typeof = function _typeof(obj) {
      return _typeof2(obj);
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : _typeof2(obj);
    };
  }

  return _typeof(obj);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

/**
 * @see https://stackoverflow.com/a/30527650/5219328
 */
var getScrollStep = function getScrollStep(targetPosition, currentPosition, scrollRate) {
  return Math.ceil((targetPosition - currentPosition) / scrollRate);
};

var getPositionsDiff = function getPositionsDiff(targetPosition, currentPosition) {
  return Math.abs(currentPosition - targetPosition);
};

var isTargetPositionReached = function isTargetPositionReached(targetPosition, currentPosition) {
  return currentPosition === targetPosition || getPositionsDiff(targetPosition, currentPosition) < 1;
};

var smoothScroll = function smoothScroll(container, targetScrollPosition) {
  var scrollRate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 5;
  var onAnimationEnd = arguments.length > 3 ? arguments[3] : undefined;

  if (!container) {
    return;
  }

  var currentScrollPosition = container.scrollTop || 0;

  if (isTargetPositionReached(targetScrollPosition, currentScrollPosition)) {
    if (typeof onAnimationEnd === "function") {
      onAnimationEnd();
    }

    return;
  }

  if (getPositionsDiff(targetScrollPosition, currentScrollPosition) < scrollRate) {
    container.scrollTop = targetScrollPosition;
  } else {
    container.scrollTop = currentScrollPosition + getScrollStep(targetScrollPosition, currentScrollPosition, scrollRate);
  }

  window.requestAnimationFrame(function () {
    return smoothScroll(container, targetScrollPosition, scrollRate, onAnimationEnd);
  });
};

var getScrollInfo = function getScrollInfo(scrollContainer) {
  var hasScrollBehavior = false;

  if (scrollContainer && scrollContainer.style && "scrollBehavior" in scrollContainer.style) {
    hasScrollBehavior = true;
  }

  var disableScrollAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return {
    disableScrollAnimation: disableScrollAnimation,
    hasScrollBehavior: hasScrollBehavior
  };
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

var ScrollContainer = /*#__PURE__*/function (_Component) {
  _inherits(ScrollContainer, _Component);

  var _super = _createSuper(ScrollContainer);

  function ScrollContainer() {
    var _this;

    _classCallCheck(this, ScrollContainer);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.removeOnScrollToListener = void 0;
    _this.containerRef = createRef();

    _this.scrollHandler = function (event) {
      emitOnScroll(event.target.scrollTop);
    };

    _this.onScroll = function (event) {
      event.persist();

      _this.throttledScroll(event);
    };

    _this.throttledScroll = throttle(_this.scrollHandler, 100);

    _this.scrollTo = function (_ref) {
      var targetPosition = _ref.targetPosition,
          _ref$scrollRate = _ref.scrollRate,
          scrollRate = _ref$scrollRate === void 0 ? 5 : _ref$scrollRate;
      var _this$state = _this.state,
          hasScrollBehavior = _this$state.hasScrollBehavior,
          disableScrollAnimation = _this$state.disableScrollAnimation;
      var scrollContainer = _this.containerRef.current;
      if (!scrollContainer) return;

      if (disableScrollAnimation || hasScrollBehavior) {
        scrollContainer.scrollTop = targetPosition;
        return;
      }

      scrollContainer.style.overflow = "hidden";
      smoothScroll(scrollContainer, targetPosition, scrollRate, function () {
        scrollContainer.style.overflow = "auto";
      });
    };

    _this.renderScrollContainer = forwardRef(_this.props.renderContainer);
    return _this;
  }

  _createClass(ScrollContainer, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.removeOnScrollToListener = onScrollTo(this.scrollTo);
      this.setState(getScrollInfo(this.containerRef.current));
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.removeOnScrollToListener && this.removeOnScrollToListener();
    }
  }, {
    key: "render",
    value: function render() {
      var RenderScrollContainer = this.renderScrollContainer,
          onScroll = this.onScroll,
          containerRef = this.containerRef;
      var _this$props = this.props,
          consumerClassName = _this$props.className,
          children = _this$props.children;
      return /*#__PURE__*/jsx(RenderScrollContainer, {
        className: cn(ScrollContainer.BASE_CLASS_NAME, consumerClassName),
        onScroll: onScroll,
        ref: containerRef,
        children: children
      });
    }
  }]);

  return ScrollContainer;
}(Component);

ScrollContainer.defaultProps = {
  renderContainer: function renderContainer(props, ref) {
    return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, props), {}, {
      ref: ref
    }));
  }
};
ScrollContainer.BASE_CLASS_NAME = "scroll-container";

export { ScrollContainer as default };
//# sourceMappingURL=scroll-container-esm.js.map
